import type { Group, Post, Comment, GroupFilters, PostFilters, User } from '../types/group'
import { GROUP_CATEGORIES, SAMPLE_GROUPS } from '../types/group'
import { isMock, db } from './firebase'
import { logger } from './logger'
import { getUserProfile } from './userService'
import { 
  collection, 
  doc, 
  setDoc, 
  deleteDoc,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  orderBy,
  limit,
  serverTimestamp,
  increment,
  DocumentData,
  Timestamp,
  writeBatch,
  DocumentReference
} from 'firebase/firestore'

// Spam detection configuration for group posts
const POST_SPAM_SIMILARITY_THRESHOLD = 0.8 // 80% similarity considered spam
const POST_SPAM_TIME_WINDOW_HOURS = 24 // Check last 24 hours
const MAX_SIMILAR_POSTS = 3 // Maximum similar posts allowed in time window

// Helper function to calculate text similarity (Jaccard index) - same as in listingService
const calculateTextSimilarity = (text1: string, text2: string): number => {
  if (!text1 || !text2) return 0
  
  // Normalize text: lowercase, remove punctuation, split into words
  const normalize = (text: string): string[] => {
    return text.toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2) // ignore short words
  }
  
  const words1 = normalize(text1)
  const words2 = normalize(text2)
  
  if (words1.length === 0 || words2.length === 0) return 0
  
  // Calculate Jaccard similarity: intersection / union
  const set1 = new Set(words1)
  const set2 = new Set(words2)
  
  const intersection = [...set1].filter(word => set2.has(word)).length
  const union = new Set([...words1, ...words2]).size
  
  return union > 0 ? intersection / union : 0
}

// Helper function to check for spam by similarity in group posts
const checkPostSpamSimilarity = async (userId: string, title: string, content: string): Promise<boolean> => {
  if (isMock) {
    // In mock mode, allow all posts
    return false
  }

  try {
    const postsRef = collection(db, GROUP_POSTS_COLLECTION)
    const now = new Date()
    const timeWindow = new Date(now.getTime() - POST_SPAM_TIME_WINDOW_HOURS * 60 * 60 * 1000)
    
    // Query recent posts by same user
    const q = query(
      postsRef,
      where('author.id', '==', userId),
      where('createdAt', '>=', timeWindow),
      orderBy('createdAt', 'desc')
    )
    
    const snapshot = await getDocs(q)
    const recentPosts: Post[] = []
    snapshot.forEach((doc) => {
      recentPosts.push(firestoreToPost(doc.id, doc.data()))
    })
    
    // Check similarity with each recent post
    let similarCount = 0
    for (const post of recentPosts) {
      const titleSimilarity = calculateTextSimilarity(title, post.title)
      const contentSimilarity = calculateTextSimilarity(content, post.content)
      const maxSimilarity = Math.max(titleSimilarity, contentSimilarity)
      
      if (maxSimilarity >= POST_SPAM_SIMILARITY_THRESHOLD) {
        similarCount++
        if (similarCount >= MAX_SIMILAR_POSTS) {
          logger.warn(`Spam detected in group posts: user ${userId} has ${similarCount} similar posts in last ${POST_SPAM_TIME_WINDOW_HOURS} hours`)
          return true
        }
      }
    }
    
    return false
  } catch (error) {
    logger.error('Error checking post spam similarity:', error)
    // If there's an error, allow the post (fail-safe)
    return false
  }
}

// Helper function to check for duplicate images in posts
const checkPostDuplicateImages = async (userId: string, attachments?: string[]): Promise<boolean> => {
  if (isMock || !attachments || attachments.length === 0) {
    return false
  }

  try {
    const postsRef = collection(db, GROUP_POSTS_COLLECTION)
    const now = new Date()
    const timeWindow = new Date(now.getTime() - POST_SPAM_TIME_WINDOW_HOURS * 60 * 60 * 1000)
    
    // Query recent posts by same user
    const q = query(
      postsRef,
      where('author.id', '==', userId),
      where('createdAt', '>=', timeWindow),
      orderBy('createdAt', 'desc')
    )
    
    const snapshot = await getDocs(q)
    const recentPosts: Post[] = []
    snapshot.forEach((doc) => {
      recentPosts.push(firestoreToPost(doc.id, doc.data()))
    })
    
    // Check for duplicate attachments (images)
    for (const post of recentPosts) {
      const postAttachments = post.attachments || []
      for (const attachment of attachments) {
        if (postAttachments.includes(attachment)) {
          logger.warn(`Duplicate attachment detected: user ${userId} reused attachment ${attachment}`)
          return true
        }
      }
    }
    
    return false
  } catch (error) {
    logger.error('Error checking duplicate attachments:', error)
    return false
  }
}

// Firestore collection names
const GROUPS_COLLECTION = 'groups'
const GROUP_POSTS_COLLECTION = 'groupPosts'
const GROUP_COMMENTS_COLLECTION = 'groupComments'
const GROUP_VOTES_COLLECTION = 'groupVotes'

const GROUP_MEMBERS_COLLECTION = 'groupMembers'
const GROUP_JOIN_REQUESTS_COLLECTION = 'groupJoinRequests'
const ACTIVITIES_COLLECTION = 'activities'

// In-memory store for mock posts (so created posts persist in mock mode)
const mockPostsStore = new Map<string, Post[]>()

// Helper functions to convert Firestore data
const firestoreToGroup = (docId: string, data: DocumentData): Group => {
  const convertTimestamp = (timestamp: unknown) => 
    timestamp instanceof Timestamp
      ? timestamp.toDate() 
      : new Date()

  return {
    id: docId,
    name: data.name || '',
    description: data.description || '',
    category: data.category || '',
    subcategory: data.subcategory,
    icon: data.icon || '👥',
    coverImage: data.coverImage,
    createdBy: data.createdBy || {
      id: '',
      name: '',
      email: '',
      role: 'member',
      joinDate: new Date(),
      postCount: 0,
      reputation: 0,
    },
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    memberCount: data.memberCount || 0,
    postCount: data.postCount || 0,
    isPublic: data.isPublic ?? true,
    isFeatured: data.isFeatured ?? false,
    rules: data.rules || [],
    tags: data.tags || [],
    recentActivity: convertTimestamp(data.recentActivity),
    moderators: data.moderators || [],
  }
}

const firestoreToPost = (docId: string, data: DocumentData): Post => {
  const convertTimestamp = (timestamp: unknown) => 
    timestamp instanceof Timestamp
      ? timestamp.toDate() 
      : new Date()

  return {
    id: docId,
    groupId: data.groupId || '',
    title: data.title || '',
    content: data.content || '',
    author: data.author || {
      id: '',
      name: '',
      email: '',
      role: 'member',
      joinDate: new Date(),
      postCount: 0,
      reputation: 0,
    },
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    upvotes: data.upvotes || 0,
    downvotes: data.downvotes || 0,
    commentCount: data.commentCount || 0,
    isPinned: data.isPinned || false,
    isLocked: data.isLocked || false,
    tags: data.tags || [],
    attachments: data.attachments,
    views: data.views || 0,
    lastActivity: convertTimestamp(data.lastActivity),
  }
}

const firestoreToComment = (docId: string, data: DocumentData): Comment => {
  const convertTimestamp = (timestamp: unknown) => 
    timestamp instanceof Timestamp
      ? timestamp.toDate() 
      : new Date()

  return {
    id: docId,
    postId: data.postId || '',
    content: data.content || '',
    author: data.author || {
      id: '',
      name: '',
      email: '',
      role: 'member',
      joinDate: new Date(),
      postCount: 0,
      reputation: 0,
    },
    createdAt: convertTimestamp(data.createdAt),
    updatedAt: convertTimestamp(data.updatedAt),
    upvotes: data.upvotes || 0,
    downvotes: data.downvotes || 0,
    parentCommentId: data.parentCommentId,
    isEdited: data.isEdited || false,
  }
}

// Mock data generation for posts
const generateMockPosts = (groupId: string): Post[] => {
  // Get stored posts for this group
  const stored = mockPostsStore.get(groupId) || []
  const defaultPosts: Post[] = [
    {
      id: 'post-1',
      groupId,
      title: 'Alguém conhece um bom corretor de imóveis em Orlando?',
      content: 'Estou procurando um corretor confiável para me ajudar a comprar uma casa em Orlando. Alguém tem recomendação?',
      author: {
        id: 'user-6',
        name: 'Fernanda Lima',
        email: 'fernanda@example.com',
        role: 'member',
        joinDate: new Date('2023-08-15'),
        postCount: 12,
        reputation: 45,
      },
      createdAt: new Date('2026-03-28'),
      updatedAt: new Date('2026-03-28'),
      upvotes: 24,
      downvotes: 2,
      commentCount: 8,
      isPinned: true,
      isLocked: false,
      tags: ['orlando', 'imoveis', 'recomendacao'],
      views: 156,
      lastActivity: new Date('2026-04-01'),
    },
    {
      id: 'post-2',
      groupId,
      title: 'Dicas para primeiro aluguel nos EUA',
      content: 'Vou me mudar para os EUA e preciso alugar meu primeiro apartamento. Quais documentos preciso? Quanto custa em média?',
      author: {
        id: 'user-7',
        name: 'Roberto Nunes',
        email: 'roberto@example.com',
        role: 'member',
        joinDate: new Date('2023-09-10'),
        postCount: 7,
        reputation: 32,
      },
      createdAt: new Date('2026-03-25'),
      updatedAt: new Date('2026-03-25'),
      upvotes: 42,
      downvotes: 1,
      commentCount: 15,
      isPinned: false,
      isLocked: false,
      tags: ['aluguel', 'documentos', 'dicas'],
      views: 289,
      lastActivity: new Date('2026-03-30'),
    },
    {
      id: 'post-3',
      groupId,
      title: 'Vendo casa em Kissimmee - 3 quartos',
      content: 'Vendo minha casa em Kissimmee, 3 quartos, 2 banheiros, piscina. R$ 1.2M. Entre em contato para mais informações.',
      author: {
        id: 'user-8',
        name: 'Pedro Alves',
        email: 'pedro@example.com',
        role: 'member',
        joinDate: new Date('2023-10-05'),
        postCount: 23,
        reputation: 78,
      },
      createdAt: new Date('2026-03-20'),
      updatedAt: new Date('2026-03-20'),
      upvotes: 18,
      downvotes: 0,
      commentCount: 5,
      isPinned: false,
      isLocked: false,
      tags: ['venda', 'kissimmee', 'casa'],
      attachments: ['https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800'],
      views: 123,
      lastActivity: new Date('2026-03-25'),
    },
  ]
  // Combine stored posts (newest first) with default posts, removing duplicates by id
  const allPosts = [...stored, ...defaultPosts]
  const uniquePosts = allPosts.filter((post, index, self) => 
    index === self.findIndex(p => p.id === post.id)
  )
  return uniquePosts
}

// Mock data for comments
const generateMockComments = (postId: string): Comment[] => {
  return [
    {
      id: 'comment-1',
      postId,
      content: 'Eu recomendo o Carlos, ele me ajudou a comprar minha casa ano passado. Muito profissional!',
      author: {
        id: 'user-9',
        name: 'Ana Costa',
        email: 'ana@example.com',
        role: 'member',
        joinDate: new Date('2023-07-12'),
        postCount: 34,
        reputation: 92,
      },
      createdAt: new Date('2026-03-28T10:30:00'),
      updatedAt: new Date('2026-03-28T10:30:00'),
      upvotes: 8,
      downvotes: 0,
      isEdited: false,
    },
    {
      id: 'comment-2',
      postId,
      content: 'Você vai precisar de passaporte, visto, comprovante de renda e histórico de crédito nos EUA.',
      author: {
        id: 'user-10',
        name: 'Lucas Santos',
        email: 'lucas@example.com',
        role: 'moderator',
        joinDate: new Date('2023-06-18'),
        postCount: 56,
        reputation: 145,
      },
      createdAt: new Date('2026-03-25T14:20:00'),
      updatedAt: new Date('2026-03-25T14:20:00'),
      upvotes: 15,
      downvotes: 1,
      isEdited: false,
    },
  ]
}

export const groupService = {
  // Get all groups with optional filters
  getGroups: async (filters?: GroupFilters): Promise<Group[]> => {
    logger.log('groupService.getGroups called with filters:', filters)
    if (isMock) {
      logger.log('Using mock mode for groups')
      // Use mock data
      let groups = [...SAMPLE_GROUPS]
      logger.log('Total mock groups:', groups.length)

      // Apply filters
      if (filters?.category) {
        logger.log('Filtering by category:', filters.category)
        groups = groups.filter(group => {
          const match = group.category === filters.category
          if (!match) logger.log(`Group ${group.id} category ${group.category} does not match`)
          return match
        })
      }

      // Apply subcategory filter
      if (filters?.subcategory) {
        logger.log('Filtering by subcategory:', filters.subcategory)
        groups = groups.filter(group => group.subcategory === filters.subcategory)
      }

      if (filters?.ids && filters.ids.length > 0) {
        groups = groups.filter(group => filters.ids!.includes(group.id))
      }

      if (filters?.search) {
        const searchLower = filters.search.toLowerCase()
        groups = groups.filter(group => 
          group.name.toLowerCase().includes(searchLower) ||
          group.description.toLowerCase().includes(searchLower) ||
          group.tags.some(tag => tag.toLowerCase().includes(searchLower))
        )
      }

      if (filters?.isPublic !== undefined) {
        groups = groups.filter(group => group.isPublic === filters.isPublic)
      }

      // Apply sorting
      if (filters?.sortBy === 'popular') {
        groups.sort((a, b) => b.memberCount - a.memberCount)
      } else if (filters?.sortBy === 'active') {
        groups.sort((a, b) => b.postCount - a.postCount)
      } else {
        // Default: recent activity
        groups.sort((a, b) => b.recentActivity.getTime() - a.recentActivity.getTime())
      }

      logger.log('Filtered groups count:', groups.length)
      return groups
    }

    // Real Firebase implementation
    try {
       const groupsRef = collection(db, GROUPS_COLLECTION)
      let q = query(groupsRef, orderBy('recentActivity', 'desc'))

      // Apply filters
      if (filters?.category) {
        q = query(q, where('category', '==', filters.category))
      }
      if (filters?.isPublic !== undefined) {
        q = query(q, where('isPublic', '==', filters.isPublic))
      }
      // Filter by IDs if provided (Firestore 'in' operator limit is 10)
      if (filters?.ids && filters.ids.length > 0) {
        if (filters.ids.length <= 10) {
          q = query(q, where('__name__', 'in', filters.ids))
        } else {
          // Too many IDs for Firestore 'in' operator, we'll filter client-side
          logger.warn('Too many IDs for Firestore "in" operator, filtering client-side')
        }
      }
      // Apply limit after filters to ensure filtered results are included
      q = query(q, limit(200))
      // Note: Firestore doesn't support multiple fields search easily.
      // We'll fetch all and filter client-side for search.
      // For better scalability, consider Algolia or similar.

      const querySnapshot = await getDocs(q)
      let groups: Group[] = []
      querySnapshot.forEach((doc) => {
        groups.push(firestoreToGroup(doc.id, doc.data()))
      })

      // Apply ID filter client-side if needed (if too many IDs or not applied via Firestore)
      if (filters?.ids && filters.ids.length > 0) {
        groups = groups.filter(group => filters.ids!.includes(group.id))
      }
      // Apply search filter client-side if needed
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase()
        groups = groups.filter(group => 
          group.name.toLowerCase().includes(searchLower) ||
          group.description.toLowerCase().includes(searchLower) ||
          group.tags.some(tag => tag.toLowerCase().includes(searchLower))
        )
      }

      // Apply subcategory filter client-side
      if (filters?.subcategory) {
        groups = groups.filter(group => group.subcategory === filters.subcategory)
      }

      // Apply sorting
      if (filters?.sortBy === 'popular') {
        groups.sort((a, b) => b.memberCount - a.memberCount)
      } else if (filters?.sortBy === 'active') {
        groups.sort((a, b) => b.postCount - a.postCount)
      } else {
        // Default: recent activity
        groups.sort((a, b) => b.recentActivity.getTime() - a.recentActivity.getTime())
      }

      return groups
    } catch (error) {
      logger.error('Error fetching groups from Firestore:', error)
      return []
    }
  },

  // Get group by ID
  getGroupById: async (groupId: string): Promise<Group | null> => {
    if (isMock) {
      const group = SAMPLE_GROUPS.find(g => g.id === groupId)
      return group || null
    }

    try {
      const groupRef = doc(db, GROUPS_COLLECTION, groupId)
      const groupSnap = await getDoc(groupRef)
      if (groupSnap.exists()) {
        return firestoreToGroup(groupSnap.id, groupSnap.data())
      }
      return null
    } catch (error) {
      logger.error('Error fetching group from Firestore:', error)
      return null
    }
  },

  // Check if user is member of a group
  isMember: async (groupId: string, userId: string): Promise<boolean> => {
    if (isMock) {
      // Mock implementation - assume user is member if userId matches creator
      const group = SAMPLE_GROUPS.find(g => g.id === groupId)
      return group ? group.createdBy.id === userId : false
    }

    try {
      const memberRef = doc(db, GROUP_MEMBERS_COLLECTION, `${userId}_${groupId}`)
      const memberSnap = await getDoc(memberRef)
      return memberSnap.exists()
    } catch (error) {
      logger.error('Error checking membership:', error)
      return false
    }
  },

  // Get posts for a group
  getPosts: async (groupId: string, filters?: PostFilters): Promise<Post[]> => {
    if (isMock) {
      const posts = generateMockPosts(groupId)

      // Apply filters
      let filteredPosts = posts

      if (filters?.authorId) {
        filteredPosts = filteredPosts.filter(post => post.author.id === filters.authorId)
      }

      if (filters?.search) {
        const searchLower = filters.search.toLowerCase()
        filteredPosts = filteredPosts.filter(post => 
          post.title.toLowerCase().includes(searchLower) ||
          post.content.toLowerCase().includes(searchLower) ||
          post.tags.some(tag => tag.toLowerCase().includes(searchLower))
        )
      }

      if (filters?.isPinned !== undefined) {
        filteredPosts = filteredPosts.filter(post => post.isPinned === filters.isPinned)
      }

      // Apply sorting
      if (filters?.sortBy === 'popular') {
        filteredPosts.sort((a, b) => (b.upvotes - b.downvotes) - (a.upvotes - a.downvotes))
      } else if (filters?.sortBy === 'top') {
        filteredPosts.sort((a, b) => b.commentCount - a.commentCount)
      } else {
        // Default: recent
        filteredPosts.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      }

      // Show pinned posts first
      filteredPosts.sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0))

      return filteredPosts
    }

    // Real Firebase implementation
    try {
      const postsRef = collection(db, GROUP_POSTS_COLLECTION)
      let q = query(postsRef, where('groupId', '==', groupId))

      // Apply filters that can be done server-side
      if (filters?.authorId) {
        q = query(q, where('author.id', '==', filters.authorId))
      }
      if (filters?.isPinned !== undefined) {
        q = query(q, where('isPinned', '==', filters.isPinned))
      }

       // Order by createdAt desc by default
      q = query(q, orderBy('createdAt', 'desc'), limit(50))

      const querySnapshot = await getDocs(q)
      let posts: Post[] = []
      querySnapshot.forEach((doc) => {
        posts.push(firestoreToPost(doc.id, doc.data()))
      })

      // Apply search filter client-side (Firestore doesn't support multi-field search)
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase()
        posts = posts.filter(post => 
          post.title.toLowerCase().includes(searchLower) ||
          post.content.toLowerCase().includes(searchLower) ||
          post.tags.some(tag => tag.toLowerCase().includes(searchLower))
        )
      }

      // Apply additional sorting (popular, top) client-side
      if (filters?.sortBy === 'popular') {
        posts.sort((a, b) => (b.upvotes - b.downvotes) - (a.upvotes - a.downvotes))
      } else if (filters?.sortBy === 'top') {
        posts.sort((a, b) => b.commentCount - a.commentCount)
      }
      // Default sorting already by createdAt desc

      // Show pinned posts first (already sorted by createdAt, need to reorder)
      posts.sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0))

      return posts
    } catch (error) {
      logger.error('Error fetching posts from Firestore:', error)
      return []
    }
  },

  // Get post by ID
  getPostById: async (postId: string): Promise<Post | null> => {
    if (isMock) {
      const groupIds = SAMPLE_GROUPS.map(g => g.id)
      for (const groupId of groupIds) {
        const posts = generateMockPosts(groupId)
        const post = posts.find(p => p.id === postId)
        if (post) return post
      }
      return null
    }

    try {
      const postRef = doc(db, GROUP_POSTS_COLLECTION, postId)
      const postSnap = await getDoc(postRef)
      if (postSnap.exists()) {
        return firestoreToPost(postSnap.id, postSnap.data())
      }
      return null
    } catch (error) {
      logger.error('Error fetching post from Firestore:', error)
      return null
    }
  },

  // Get comments for a post
  getComments: async (postId: string): Promise<Comment[]> => {
    if (isMock) {
      return generateMockComments(postId)
    }

    try {
      const commentsRef = collection(db, GROUP_COMMENTS_COLLECTION)
      const q = query(commentsRef, where('postId', '==', postId), orderBy('createdAt', 'asc'))
      const querySnapshot = await getDocs(q)
      const comments: Comment[] = []
      querySnapshot.forEach((doc) => {
        comments.push(firestoreToComment(doc.id, doc.data()))
      })
      return comments
    } catch (error) {
      logger.error('Error fetching comments from Firestore:', error)
      return []
    }
  },

  // Get all categories
  getCategories: async () => {
    return GROUP_CATEGORIES
  },

  // Create a new group
   createGroup: async (groupData: Omit<Group, 'id' | 'createdAt' | 'updatedAt' | 'memberCount' | 'postCount' | 'recentActivity' | 'moderators'>, userId: string): Promise<Group> => {
     // Validate required fields
     if (!groupData.coverImage) {
       throw new Error('Imagem de capa é obrigatória')
     }
      if (isMock) {
      const newGroup: Group = {
        ...groupData,
        id: `group-${Date.now()}`,
        createdBy: {
          ...groupData.createdBy,
          id: userId,
          role: 'admin' as const,
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        memberCount: 1,
        postCount: 0,
        isPublic: groupData.isPublic ?? true,
        isFeatured: false,
        rules: groupData.rules || [],
        tags: groupData.tags || [],
        recentActivity: new Date(),
        moderators: [],
      }
      logger.log('Created group:', newGroup)
      return newGroup
    }

    try {
      const groupsRef = collection(db, GROUPS_COLLECTION)
      const groupToSave = {
        ...groupData,
         createdBy: {
          ...groupData.createdBy,
          id: userId,
          role: 'admin' as const,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        memberCount: 1,
        postCount: 0,
        isPublic: groupData.isPublic ?? true,
        isFeatured: false,
        rules: groupData.rules || [],
        tags: groupData.tags || [],
        recentActivity: serverTimestamp(),
        moderators: [],
      }
      const docRef = await addDoc(groupsRef, groupToSave)

      // Add creator as member
      const memberRef = doc(db, GROUP_MEMBERS_COLLECTION, `${userId}_${docRef.id}`)
      await setDoc(memberRef, {
        userId,
        groupId: docRef.id,
        joinedAt: serverTimestamp(),
        role: 'admin',
      })

      // Create activity
      const activityRef = doc(collection(db, ACTIVITIES_COLLECTION))
      await setDoc(activityRef, {
        userId,
        groupId: docRef.id,
        type: 'group_created',
        title: 'Criou um novo grupo',
        description: `Grupo: ${groupData.name}`,
        date: serverTimestamp(),
        link: `/groups/${docRef.id}`,
      })

      const newGroup: Group = {
        ...groupData,
        id: docRef.id,
        createdBy: groupToSave.createdBy,
        createdAt: new Date(),
        updatedAt: new Date(),
        memberCount: 1,
        postCount: 0,
        isPublic: groupData.isPublic ?? true,
        isFeatured: false,
        rules: groupData.rules || [],
        tags: groupData.tags || [],
        recentActivity: new Date(),
        moderators: [],
      }
      return newGroup
    } catch (error) {
      logger.error('Error creating group in Firestore:', error)
      throw error
    }
  },

  // Join a group
  joinGroup: async (groupId: string, userId: string): Promise<boolean> => {
    if (isMock) {
      // Check if already member (simplified mock)
      const group = SAMPLE_GROUPS.find(g => g.id === groupId)
      if (!group) return false
      const isAlreadyMember = group.createdBy.id === userId
      if (isAlreadyMember) return true
      // Mock join logic based on group privacy
      if (group.isPublic) {
        logger.log(`User ${userId} joined public group ${groupId}`)
      } else {
        logger.log(`User ${userId} requested to join private group ${groupId}`)
      }
      return true
    }

    try {
      // Check if already member
      const memberRef = doc(db, GROUP_MEMBERS_COLLECTION, `${userId}_${groupId}`)
      const memberSnap = await getDoc(memberRef)
      if (memberSnap.exists()) return true

      // Fetch group to check existence and privacy
      const groupRef = doc(db, GROUPS_COLLECTION, groupId)
      const groupSnap = await getDoc(groupRef)
      if (!groupSnap.exists()) return false
      const groupData = groupSnap.data()
      const isPublic = groupData.isPublic ?? true

      if (isPublic) {
        // Public group: join directly
        await setDoc(memberRef, {
          userId,
          groupId,
          joinedAt: serverTimestamp(),
          role: 'member',
        })
        // Update group member count
        await updateDoc(groupRef, {
          memberCount: increment(1),
          recentActivity: serverTimestamp(),
        })
        // Create activity for user joining group
        const activityRef = doc(collection(db, ACTIVITIES_COLLECTION))
        await setDoc(activityRef, {
          userId,
          groupId,
          type: 'group_joined',
          title: 'Entrou em um grupo',
          description: `Grupo: ${groupId}`,
          date: serverTimestamp(),
          link: `/groups/${groupId}`,
        })
      } else {
        // Private group: create join request
        const requestsRef = collection(db, GROUP_JOIN_REQUESTS_COLLECTION)
        await addDoc(requestsRef, {
          userId,
          groupId,
          status: 'pending',
          createdAt: serverTimestamp(),
        })
        // Create activity for join request
        const activityRef = doc(collection(db, ACTIVITIES_COLLECTION))
        await setDoc(activityRef, {
          userId,
          groupId,
          type: 'group_join_requested',
          title: 'Solicitou entrada em grupo privado',
          description: `Grupo: ${groupId}`,
          date: serverTimestamp(),
          link: `/groups/${groupId}`,
        })
      }
      return true
    } catch (error) {
      logger.error('Error joining group:', error)
      return false
    }
  },

  // Leave a group
  leaveGroup: async (groupId: string, userId: string): Promise<boolean> => {
    if (isMock) {
      logger.log(`User ${userId} left group ${groupId}`)
      return true
    }

    try {
      const memberRef = doc(db, GROUP_MEMBERS_COLLECTION, `${userId}_${groupId}`)
      await deleteDoc(memberRef)
      // Update group member count
      const groupRef = doc(db, GROUPS_COLLECTION, groupId)
      await updateDoc(groupRef, {
        memberCount: increment(-1),
        recentActivity: serverTimestamp(),
      })
      return true
    } catch (error) {
      logger.error('Error leaving group:', error)
      return false
    }
  },

  // Delete a group (only creator/admin)
  deleteGroup: async (groupId: string, userId: string): Promise<boolean> => {
    if (isMock) {
      logger.log(`Mock: deleting group ${groupId} by user ${userId}`)
      return true
    }

    try {
       // Fetch group to check creator
      const groupRef = doc(db, GROUPS_COLLECTION, groupId)
      const groupSnap = await getDoc(groupRef)
      if (!groupSnap.exists()) {
        logger.error('Group not found:', groupId)
        return false
      }
      const groupData = groupSnap.data()
      const creatorId = groupData.createdBy?.id
      if (!creatorId) {
        logger.error('Group has no creator')
        return false
      }
      const moderators = (groupData.moderators || []) as User[]
      const isCreator = creatorId === userId
      const isModerator = moderators.some((m) => m.id === userId)
      // Check if user is global admin
      let isGlobalAdmin = false
      if (!isCreator && !isModerator) {
        try {
          const userProfile = await getUserProfile(userId)
          if (userProfile && (userProfile.isAdmin || userProfile.role === 'admin')) {
            isGlobalAdmin = true
          }
        } catch (error) {
          logger.error('Error fetching user profile:', error)
        }
      }
      if (!isCreator && !isModerator && !isGlobalAdmin) {
        logger.error('User is not authorized to delete the group')
        return false
      }

      // Use batch for atomic deletion
      const batch = writeBatch(db)

      // Delete group document
      batch.delete(groupRef)

      // Delete memberships
      const membersRef = collection(db, GROUP_MEMBERS_COLLECTION)
      const membersQuery = query(membersRef, where('groupId', '==', groupId))
      const membersSnapshot = await getDocs(membersQuery)
      membersSnapshot.forEach((doc) => {
        batch.delete(doc.ref)
      })

      // Delete join requests
      const joinRequestsRef = collection(db, GROUP_JOIN_REQUESTS_COLLECTION)
      const joinRequestsQuery = query(joinRequestsRef, where('groupId', '==', groupId))
      const joinRequestsSnapshot = await getDocs(joinRequestsQuery)
      joinRequestsSnapshot.forEach((doc) => {
        batch.delete(doc.ref)
      })

      // Delete posts
      const postsRef = collection(db, GROUP_POSTS_COLLECTION)
      const postsQuery = query(postsRef, where('groupId', '==', groupId))
      const postsSnapshot = await getDocs(postsQuery)
      postsSnapshot.forEach((doc) => {
        batch.delete(doc.ref)
      })

      // Note: Comments are not deleted to avoid complex queries
      // They will remain orphaned but can be cleaned up separately


      // Delete activities related to the group
      const activitiesRef = collection(db, ACTIVITIES_COLLECTION)
      const activitiesQuery = query(activitiesRef, where('groupId', '==', groupId))
      const activitiesSnapshot = await getDocs(activitiesQuery)
      activitiesSnapshot.forEach((doc) => {
        batch.delete(doc.ref)
      })

      await batch.commit()
      return true
    } catch (error) {
      logger.error('Error deleting group:', error)
      return false
    }
  },

  // Get user's subscribed group IDs
  getUserGroupIds: async (userId: string): Promise<string[]> => {
    if (isMock) {
      // Return mock group IDs from sample groups
      return SAMPLE_GROUPS.slice(0, 3).map(g => g.id)
    }

    try {
      const membersRef = collection(db, GROUP_MEMBERS_COLLECTION)
      const q = query(membersRef, where('userId', '==', userId))
      const querySnapshot = await getDocs(q)
      const groupIds: string[] = []
      querySnapshot.forEach((doc) => {
        const data = doc.data()
        if (data.groupId) groupIds.push(data.groupId)
      })
      return groupIds
    } catch (error) {
      logger.error('Error fetching user group IDs:', error)
      return []
    }
  },

  // Create a new post
  createPost: async (groupId: string, postData: Omit<Post, 'id' | 'createdAt' | 'updatedAt' | 'upvotes' | 'downvotes' | 'commentCount' | 'views' | 'lastActivity'>): Promise<Post> => {
    if (isMock) {
      const newPost: Post = {
        ...postData,
        id: `post-${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date(),
        upvotes: 0,
        downvotes: 0,
        commentCount: 0,
        views: 0,
        lastActivity: new Date(),
      }
      logger.log('Created post:', newPost)
      // Store in mockPostsStore for persistence
      const stored = mockPostsStore.get(groupId) || []
      mockPostsStore.set(groupId, [...stored, newPost])
      logger.log(`Post stored for group ${groupId}. Total posts: ${stored.length + 1}`)
      return newPost
    }

    // Check for spam similarity and duplicate attachments
    const isSpam = await checkPostSpamSimilarity(postData.author.id, postData.title, postData.content)
    const hasDuplicateAttachments = await checkPostDuplicateImages(postData.author.id, postData.attachments)
    
    if (isSpam) {
      throw new Error('Spam detected: Your post is too similar to previous posts you created recently. Please modify the title or content.')
    }
    
    if (hasDuplicateAttachments) {
      throw new Error('Spam detected: You are reusing attachments from previous posts. Please upload new images.')
    }

    try {
      const postsRef = collection(db, GROUP_POSTS_COLLECTION)
      // Remove undefined fields to avoid Firestore errors
      const cleanedPostData: Record<string, any> = {} // eslint-disable-line @typescript-eslint/no-explicit-any
      for (const [key, value] of Object.entries(postData)) {
        if (value !== undefined) {
          cleanedPostData[key] = value
        }
      }
      const postToSave = {
        ...cleanedPostData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        upvotes: 0,
        downvotes: 0,
        commentCount: 0,
        views: 0,
        lastActivity: serverTimestamp(),
      }
      const docRef = await addDoc(postsRef, postToSave)
      
      // Update group's post count
      const groupRef = doc(db, GROUPS_COLLECTION, groupId)
      await updateDoc(groupRef, {
        postCount: increment(1),
        recentActivity: serverTimestamp(),
      })

      const newPost: Post = {
        ...postData,
        id: docRef.id,
        createdAt: new Date(),
        updatedAt: new Date(),
        upvotes: 0,
        downvotes: 0,
        commentCount: 0,
        views: 0,
        lastActivity: new Date(),
      }
      return newPost
    } catch (error) {
      logger.error('Error creating post in Firestore:', error)
      throw error
    }
  },

  // Add a comment
  addComment: async (postId: string, commentData: Omit<Comment, 'id' | 'createdAt' | 'updatedAt' | 'upvotes' | 'downvotes' | 'isEdited'>): Promise<Comment> => {
    if (isMock) {
      const newComment: Comment = {
        ...commentData,
        id: `comment-${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date(),
        upvotes: 0,
        downvotes: 0,
        isEdited: false,
      }
      logger.log('Added comment:', newComment)
      return newComment
    }

    try {
      const commentsRef = collection(db, GROUP_COMMENTS_COLLECTION)
      const commentToSave = {
        ...commentData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        upvotes: 0,
        downvotes: 0,
        isEdited: false,
      }
      const docRef = await addDoc(commentsRef, commentToSave)

      // Update post's comment count and last activity
      const postRef = doc(db, GROUP_POSTS_COLLECTION, postId)
      await updateDoc(postRef, {
        commentCount: increment(1),
        lastActivity: serverTimestamp(),
      })

      // Update group's recent activity
      const postSnap = await getDoc(postRef)
      if (postSnap.exists()) {
        const post = postSnap.data()
        const groupRef = doc(db, GROUPS_COLLECTION, post.groupId)
        await updateDoc(groupRef, {
          recentActivity: serverTimestamp(),
        })
      }

      const newComment: Comment = {
        ...commentData,
        id: docRef.id,
        createdAt: new Date(),
        updatedAt: new Date(),
        upvotes: 0,
        downvotes: 0,
        isEdited: false,
      }
      return newComment
    } catch (error) {
      logger.error('Error adding comment to Firestore:', error)
      throw error
    }
  },

  // Upvote/downvote a post or comment (one vote per user per item)
  vote: async (itemId: string, userId: string, voteType: 'upvote' | 'downvote'): Promise<boolean> => {
    if (isMock) {
      logger.log(`User ${userId} ${voteType}d item ${itemId}`)
      return true
    }

    try {
      // Determine if item is post or comment
      let itemRef: DocumentReference | null = null
      let itemType: 'post' | 'comment' = 'post'
      
      const postRef = doc(db, GROUP_POSTS_COLLECTION, itemId)
      const postSnap = await getDoc(postRef)
      if (postSnap.exists()) {
        itemRef = postRef
        itemType = 'post'
      } else {
        const commentRef = doc(db, GROUP_COMMENTS_COLLECTION, itemId)
        const commentSnap = await getDoc(commentRef)
        if (commentSnap.exists()) {
          itemRef = commentRef
          itemType = 'comment'
        } else {
          logger.error('Item not found for voting:', itemId)
          return false
        }
      }

      // Check existing vote
      const voteRef = doc(db, GROUP_VOTES_COLLECTION, `${itemId}_${userId}`)
      const voteSnap = await getDoc(voteRef)
      
      if (voteSnap.exists()) {
        const existingVote = voteSnap.data()
        const existingType = existingVote.voteType
        
        if (existingType === voteType) {
          // Same vote type: remove vote
          await deleteDoc(voteRef)
          // Decrement the count
          await updateDoc(itemRef, {
            [voteType === 'upvote' ? 'upvotes' : 'downvotes']: increment(-1)
          })
        } else {
          // Different vote type: change vote
          await updateDoc(voteRef, {
            voteType,
            updatedAt: serverTimestamp()
          })
          // Decrement old vote, increment new vote
          await updateDoc(itemRef, {
            [existingType === 'upvote' ? 'upvotes' : 'downvotes']: increment(-1),
            [voteType === 'upvote' ? 'upvotes' : 'downvotes']: increment(1)
          })
        }
      } else {
        // New vote
        await setDoc(voteRef, {
          itemId,
          userId,
          itemType,
          voteType,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        // Increment the count
        await updateDoc(itemRef, {
          [voteType === 'upvote' ? 'upvotes' : 'downvotes']: increment(1)
        })
      }
      
      return true
    } catch (error) {
      logger.error('Error voting on item:', error)
      return false
    }
  },

  // Increment post views count
  incrementPostViews: async (postId: string): Promise<void> => {
    if (isMock) {
      logger.log('Mock: increment post views', postId)
      return
    }

    try {
      const postRef = doc(db, GROUP_POSTS_COLLECTION, postId)
      await updateDoc(postRef, {
        views: increment(1),
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      logger.error('Error incrementing post views in Firestore:', error)
    }
  },



  // Delete a post (for moderator actions)
  deletePost: async (postId: string, userId?: string): Promise<void> => {
    if (isMock) {
      logger.log(`Mock: deleting post ${postId} by user ${userId}`)
      // Remove from mockPostsStore
      for (const [groupId, posts] of mockPostsStore.entries()) {
        const filteredPosts = posts.filter(post => post.id !== postId)
        if (filteredPosts.length !== posts.length) {
          mockPostsStore.set(groupId, filteredPosts)
          logger.log(`Post ${postId} removed from group ${groupId}`)
          return
        }
      }
      logger.log(`Post ${postId} not found in mock store`)
      return
    }
    try {
      // First get post to know groupId and author
      const postRef = doc(db, GROUP_POSTS_COLLECTION, postId)
      const postSnap = await getDoc(postRef)
      if (!postSnap.exists()) {
        throw new Error('Post not found')
      }
      const post = postSnap.data()
      const groupId = post.groupId
      const authorId = post.author?.id

      // If userId is provided, check permissions
      if (userId) {
        // Check if user is the author
        if (authorId !== userId) {
          // Check if user is moderator or admin of the group
          const groupRef = doc(db, GROUPS_COLLECTION, groupId)
          const groupSnap = await getDoc(groupRef)
          if (groupSnap.exists()) {
            const groupData = groupSnap.data()
            const moderators = (groupData.moderators || []) as User[]
            const isModerator = moderators.some((m) => m.id === userId)
            const isCreator = groupData.createdBy?.id === userId
            
            if (!isModerator && !isCreator) {
              // Check if user is global admin
              const userProfile = await getUserProfile(userId)
              const isAdmin = userProfile?.isAdmin || userProfile?.role === 'admin'
              if (!isAdmin) {
                throw new Error('You do not have permission to delete this post')
              }
            }
          } else {
            throw new Error('Group not found')
          }
        }
      }

      await deleteDoc(postRef)

      // Update group's post count
      const groupRef = doc(db, GROUPS_COLLECTION, groupId)
      await updateDoc(groupRef, {
        postCount: increment(-1),
        recentActivity: serverTimestamp(),
      })
    } catch (error) {
      logger.error('Error deleting post:', error)
      throw error
    }
  },

  // Get user's post count
  getUserPostCount: async (userId: string): Promise<number> => {
    if (isMock) {
      // Return mock count
      return 42
    }
    try {
      const postsRef = collection(db, GROUP_POSTS_COLLECTION)
      const q = query(postsRef, where('author.id', '==', userId))
      const snapshot = await getDocs(q)
      return snapshot.size
    } catch (error) {
      logger.error('Error fetching user post count:', error)
      return 0
    }
  },

  // Get user's comment count
  getUserCommentCount: async (userId: string): Promise<number> => {
    if (isMock) {
      // Return mock count
      return 156
    }
    try {
      const commentsRef = collection(db, GROUP_COMMENTS_COLLECTION)
      const q = query(commentsRef, where('author.id', '==', userId))
      const snapshot = await getDocs(q)
      return snapshot.size
    } catch (error) {
      logger.error('Error fetching user comment count:', error)
      return 0
    }
  },

   // Get user's group count (already have getUserGroupIds, but add count function)
   getUserGroupCount: async (userId: string): Promise<number> => {
     if (isMock) {
       // Return mock count
       return 8
     }
     try {
       const membersRef = collection(db, GROUP_MEMBERS_COLLECTION)
       const q = query(membersRef, where('userId', '==', userId))
       const snapshot = await getDocs(q)
       return snapshot.size
     } catch (error) {
       logger.error('Error fetching user group count:', error)
       return 0
     }
   },

   // Moderator/Admin functions
   forceJoinGroup: async (groupId: string, userId: string, asModerator: boolean = false): Promise<boolean> => {
     if (isMock) {
       logger.log(`Mock: User ${userId} forced to join group ${groupId} as ${asModerator ? 'moderator' : 'member'}`)
       return true
     }
     try {
       // Check if already member
       const memberRef = doc(db, GROUP_MEMBERS_COLLECTION, `${userId}_${groupId}`)
       const memberSnap = await getDoc(memberRef)
       if (memberSnap.exists()) {
         // Already member, update role if needed
         await updateDoc(memberRef, {
           role: asModerator ? 'moderator' : 'member'
         })
         return true
       }
       // Fetch group to check existence
       const groupRef = doc(db, GROUPS_COLLECTION, groupId)
       const groupSnap = await getDoc(groupRef)
       if (!groupSnap.exists()) return false
       // Join directly with specified role
       await setDoc(memberRef, {
         userId,
         groupId,
         joinedAt: serverTimestamp(),
         role: asModerator ? 'moderator' : 'member',
       })
       // Update group member count
       await updateDoc(groupRef, {
         memberCount: increment(1),
         recentActivity: serverTimestamp(),
       })
       // Create activity for user joining group (forced)
       const activityRef = doc(collection(db, ACTIVITIES_COLLECTION))
       await setDoc(activityRef, {
         userId,
         groupId,
         type: 'group_joined_forced',
         title: 'Entrou em um grupo (forçado)',
         description: `Grupo: ${groupId}`,
         timestamp: serverTimestamp(),
       })
       return true
     } catch (error) {
       logger.error('Error forcing join group:', error)
       return false
     }
   },

   removeMemberFromGroup: async (groupId: string, userId: string): Promise<boolean> => {
     if (isMock) {
       logger.log(`Mock: User ${userId} removed from group ${groupId}`)
       return true
     }
     try {
       const memberRef = doc(db, GROUP_MEMBERS_COLLECTION, `${userId}_${groupId}`)
       const memberSnap = await getDoc(memberRef)
       if (!memberSnap.exists()) return false
       // Delete member record
       await deleteDoc(memberRef)
       // Update group member count
       const groupRef = doc(db, GROUPS_COLLECTION, groupId)
       await updateDoc(groupRef, {
         memberCount: increment(-1),
         recentActivity: serverTimestamp(),
       })
       // Create activity for member removal
       const activityRef = doc(collection(db, ACTIVITIES_COLLECTION))
       await setDoc(activityRef, {
         userId,
         groupId,
         type: 'member_removed',
         title: 'Membro removido do grupo',
         description: `Usuário ${userId} removido do grupo ${groupId}`,
         timestamp: serverTimestamp(),
       })
       return true
     } catch (error) {
       logger.error('Error removing member from group:', error)
       return false
     }
   },

    banUserFromGroup: async (groupId: string, userId: string, reason?: string): Promise<boolean> => {
      if (isMock) {
        logger.log(`Mock: User ${userId} banned from group ${groupId}, reason: ${reason}`)
        return true
      }
      try {
        // First remove member if exists
        const memberRef = doc(db, GROUP_MEMBERS_COLLECTION, `${userId}_${groupId}`)
        const memberSnap = await getDoc(memberRef)
        if (memberSnap.exists()) {
          await deleteDoc(memberRef)
          // Update group member count
          const groupRef = doc(db, GROUPS_COLLECTION, groupId)
          await updateDoc(groupRef, {
            memberCount: increment(-1),
            recentActivity: serverTimestamp(),
          })
        }
        // Add to banned collection
        const bannedRef = doc(db, 'groupBans', `${userId}_${groupId}`)
        await setDoc(bannedRef, {
          userId,
          groupId,
          reason,
          bannedAt: serverTimestamp(),
        })
        // Create activity for ban
        const activityRef = doc(collection(db, ACTIVITIES_COLLECTION))
        await setDoc(activityRef, {
          userId,
          groupId,
          type: 'user_banned',
          title: 'Usuário banido do grupo',
          description: `Usuário ${userId} banido do grupo ${groupId}${reason ? `, motivo: ${reason}` : ''}`,
          timestamp: serverTimestamp(),
        })
        return true
      } catch (error) {
        logger.error('Error banning user from group:', error)
        return false
      }
    },

   // Check if user is banned from group
    isUserBannedFromGroup: async (groupId: string, userId: string): Promise<boolean> => {
      if (isMock) {
        return false
      }
      try {
        const bannedRef = doc(db, 'groupBans', `${userId}_${groupId}`)
        const bannedSnap = await getDoc(bannedRef)
        return bannedSnap.exists()
      } catch (error) {
        logger.error('Error checking ban status:', error)
        return false
      }
    },

    // Get all members of a group
    getGroupMembers: async (groupId: string): Promise<User[]> => {
      if (isMock) {
        return []
      }
      try {
        const membersRef = collection(db, GROUP_MEMBERS_COLLECTION)
        const q = query(membersRef, where('groupId', '==', groupId))
        const querySnapshot = await getDocs(q)
        const members: User[] = []
        for (const doc of querySnapshot.docs) {
          const data = doc.data()
          const userProfile = await getUserProfile(data.userId)
          if (userProfile) {
            members.push({
              id: userProfile.uid,
              name: userProfile.displayName || '',
              email: userProfile.email || '',
              role: data.role || 'member',
              joinDate: data.joinedAt?.toDate() || new Date(),
              postCount: 0,
              reputation: 0
            })
          }
        }
        return members
      } catch (error) {
        logger.error('Error fetching group members:', error)
        return []
      }
    },

    // Get all banned users from a group
    getGroupBans: async (groupId: string): Promise<Array<{userId: string, reason?: string, bannedAt: Date}>> => {
      if (isMock) {
        return []
      }
      try {
        const bansRef = collection(db, 'groupBans')
        const q = query(bansRef, where('groupId', '==', groupId))
        const querySnapshot = await getDocs(q)
        return querySnapshot.docs.map(doc => {
          const data = doc.data()
          return {
            userId: data.userId,
            reason: data.reason,
            bannedAt: data.bannedAt?.toDate() || new Date()
          }
        })
      } catch (error) {
        logger.error('Error fetching group bans:', error)
        return []
      }
    },

    // Unban a user from a group
    unbanUserFromGroup: async (groupId: string, userId: string): Promise<boolean> => {
      if (isMock) {
        logger.log(`Mock: User ${userId} unbanned from group ${groupId}`)
        return true
      }
      try {
        const bannedRef = doc(db, 'groupBans', `${userId}_${groupId}`)
        await deleteDoc(bannedRef)
        return true
      } catch (error) {
        logger.error('Error unbanning user from group:', error)
        return false
      }
    },
  }